import { memo, useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../store/Hooks";
import { setPdfExportObject } from "../../store/SiteSlice";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "react-query";
import {
    Button, Col, Flex, Form, Row, Select, Skeleton, message, DatePicker,
    Input,
    Space,
    Spin,
    Tooltip,
    Card
} from "antd";
import { SFModal } from "../../components/elements/SFModal";
import { mdlReservation } from "../../models/domain/Reservation";
import { SyncOutlined, WindowsOutlined } from '@ant-design/icons';
import { PdfIcon } from "../../components/common/CustomIcons";
import { toast } from "react-toastify";
import { enmConfirmationStatus } from "../../models/enums/ConfirmationStatus";
import _ from "lodash";
import { mdlPDFRendererProps } from "../../models/ui-models/PDFRendererProps";
import { enmPdfRendererType } from "../../models/enums/PdfRenderer";
import { enmPdfExportType } from "../../models/enums/PdfExportType";
import { nanoid } from "@reduxjs/toolkit";
import { mdlEntryListFilterRequest } from "../../models/service-models/entry-list/EntryListFilterRequest";
import { HotelSelect } from "../../components/common/HotelSelect";
import { ConfirmStatusSelect } from "../../components/common/ConfirmStatusSelect";
import { AgencySelect } from "../../components/common/AgencySelect";
import { EntryListTable } from "../../components/entry-list/EntryListTable";
import { EntryListService } from "../../services/EntryListService";
import { ObjectHelper } from "../../utils/ObjectHelper";
import { ExportModal } from "../../components/common/ExportModal";
import { mdlReservationEntryItem } from "../../models/domain/ReservationEntryItem";
import { mdlReservationEntryViewItem } from "../../models/domain/ReservationEntryViewItem";
import { mdlCustomer } from "../../models/domain/Customer";
import { enmExportStatus } from "../../models/enums/ExportStatus";
import { AgencySelectByHotelPaxId } from "../../components/agencies/AgencySelectByHotelPaxId";
import { DateRangePicker } from "../../components/common/DateRangePicker";
import dayjs, { Dayjs } from "dayjs";
import { mdlHotel } from "../../models/domain/Hotel";
import { mdlAgencyListByPaxIdRequest } from "../../models/service-models/agency/AgencyListByPaxIdRequest";
import { AgencyService } from "../../services/AgencyService";
import { SFDatePicker } from "../../components/common/DatePicker";
import { enmExcelExportType } from "../../utils/ExcelColumnMappings";


export const EntryList = memo(function EntryList() {
    const dispatch = useAppDispatch();
    const { t } = useTranslation();

    const store = useAppSelector(x => x.site);
    const [openExcelExportModal, setOpenExcelExportModal] = useState<boolean>(false);
    const [selectedHotel, setSelectedHotel] = useState<string>();
    const [messageApi, contextHolder] = message.useMessage();
    const [dateFilterType, setDateFilterType] = useState<string>("1");
    const [form] = Form.useForm();
    const dateFormat = 'DD.MM.YYYY';
    const { RangePicker } = DatePicker;
    const [reservationViews, setReservationViews] = useState<Array<any>>();
    const userHotels = useAppSelector(x => x.site.userHotels);
    const [selectedHotelPaxId, setSelectedHotelPaxId] = useState<number>();

    const { mutateAsync: getEntryList, isLoading, data } = useMutation(
        (request: mdlEntryListFilterRequest) => EntryListService.List(request),
        {
            onMutate(variables) {
                variables.hotelPaximumId = selectedHotelPaxId;
                // if(variables.checkInFrom){
                //     variables.checkInFrom = new Date(variables.checkInFrom.year(),variables.checkInFrom.month(),variables.checkInFrom.date());
                // }
            },
            onError: error => {
                console.log(error);
            },
            onSuccess(data, variables, context) {
                if (data.header && !data.header.success) {
                    toast.error(ObjectHelper.getErrorMessage(data.header.messages));
                }
                else {
                    var result = data.body!.data!.flatMap((reservation: mdlReservationEntryItem, index: number) =>
                        reservation.customers!.map((customer: mdlCustomer, customerIndex: number) => ({
                            key: `${index}-${customerIndex}`,
                            operator: reservation.operator,
                            adult: reservation.adult,
                            child: reservation.child,
                            infant: reservation.infant,
                            room: reservation.room,
                            confirmationStatus: reservation.confirmationStatus,
                            hotelName: reservation.hotelName,
                            agencyName: reservation.agencyName,
                            voucher: reservation.voucher,
                            checkInDate: reservation.checkInDate,
                            checkOutDate: reservation.checkOutDate,
                            roomCount: reservation.roomCount,
                            night: reservation.night,
                            roomName: reservation.roomName,
                            roomType: reservation.roomType,
                            accomType: reservation.accomType,
                            status: reservation.status,
                            sellDate: reservation.sellDate,
                            agencyLogo: reservation.agencyLogo,
                            title: customer.title,
                            name: customer.name,
                            nationality: customer.nationality,
                            nationalityString: customer.nationalityString,
                            birthDate: customer.birthDate,
                            birthDateString: customer.birthDateString,
                            age: customer.age,
                            arrival: customer.arrival,
                            arrivalYon1: customer.arrivalYon1,
                            departure: customer.departure,
                            departureYon1: customer.departureYon1,
                            arrivalTrfNo: customer.arrivalTrfNo,
                            departureTrfNo: customer.departureTrfNo,
                            musNo: customer.musNo,
                            arrivalTime: customer.arrivalTime ?? undefined,
                            departureTime: customer.departureTime,
                            arrivalTimeString: customer.arrivalTimeString,
                            departureTimeString: customer.departureTimeString,
                            musTakipNo: customer.musTakipNo,
                            subVoucherNo: customer.subVoucherNo
                        })));
                    setReservationViews(result as mdlReservationEntryViewItem[])
                }
            }
        }
    );

    useEffect(() => {
        form.setFieldsValue({ ...form, "pageSize": 100, "pageNumber": 1 });
    }, [data]);


    const handlePaginationChange = (page: number, pageSize: number) => {
        var pSize = form.getFieldValue("pageSize");
        if (pSize != pageSize)
            form.setFieldsValue({ ...form, "pageSize": pageSize ?? 100, "pageNumber": 1 });
        else
            form.setFieldsValue({ ...form, "pageSize": pageSize ?? 100, "pageNumber": page ?? 1 });
        form.submit();
    }
    //#endregion

    //#region HELPERS 
    const exportToPDF = (type: enmPdfExportType, entryList: Array<mdlReservationEntryItem>) => {
        var exportProps: mdlPDFRendererProps = {
            params: { reservationEntryList: entryList },
            rendererType: enmPdfRendererType.EntryList,
            type: type
        }
        dispatch(setPdfExportObject(exportProps));
    }
    //#endregion

    const onFocus = () => {
        let i;
        const el = document.getElementsByClassName(
            "ant-select-selection-search-input"
        );
        for (i = 0; i < el.length; i++) {
            el[i].setAttribute(
                "autocomplete",
                "registration-select"
            );
        }
    }

    const handleDateFilterTypeChange = (value: string) => {
        form.setFieldValue("dateFilterType", value);
        setDateFilterType(value);
    }

    const handleHotelChange = (value: string) => {
        var found = _.find(userHotels, function (h) { return value == h.id });
        if (found)
            setSelectedHotelPaxId(found.paximumId);
        setSelectedHotel(value);
        form.setFieldValue("hotelId", value);
    }

    const getDefaultDateValues = (field: string) => {
        var date = form.getFieldValue(field);
        if (date && dayjs(date).isValid()) {
            return dayjs(
                date,
                "YYYY-MM-DD"
            )
        }
        else {
            return undefined;
        }
    }

    const {
        data: agenciesData,
        isLoading: agenciesLoading,
        isError: agenciesError,
        refetch: agenciesRefetch
    } = useQuery(
        ["getAgenciesByPaxId", selectedHotel],
        () => {
            if (selectedHotel != null && userHotels) {
                var paxIds = _.filter(userHotels, function (hotel) {
                    return selectedHotel == hotel.id;
                }).map(x => (x as mdlHotel).paximumId)
                var request: mdlAgencyListByPaxIdRequest = { paxIds: paxIds, crossCheck: false };
                return AgencyService.ListByPaxId(request);
            }

        },
        {
            onSuccess(data) {
                form.setFieldValue("agencyIds", data?.body?.data.map(x => x.id));
            },
            refetchOnWindowFocus: false,
            enabled: !!selectedHotel
        }
    );


    return (
        <>
            {contextHolder}
            <div className="nk-content ">
                <div className="container-fluid">
                    <Spin spinning={store.exportStatus != enmExportStatus.DONE}>
                        <div className="nk-content-inner">
                            <div className="nk-content-body">
                                <div className="nk-block">
                                    <div className="row g-gs">
                                        <div className="col-12">
                                            <div className="card card-bordered">
                                                <div className="card-inner">
                                                    {
                                                        <Flex vertical>
                                                            <Form
                                                                form={form}
                                                                key={nanoid()}
                                                                name="entry-filter-form"
                                                                layout="vertical"
                                                                onFinish={async (variables) => await getEntryList(variables)}
                                                                autoComplete="off"
                                                            >
                                                                <Row gutter={16}>
                                                                    <Col span={4}>
                                                                        <Form.Item< mdlEntryListFilterRequest>
                                                                            label={t("entryList.hotel")}
                                                                            name="hotelId"
                                                                            rules={[{ required: true, message: t("common.required") }]}
                                                                        >
                                                                            <HotelSelect onChange={(value: string) => handleHotelChange(value)} />
                                                                        </Form.Item>
                                                                    </Col>
                                                                    {
                                                                        selectedHotel
                                                                        &&
                                                                        <>
                                                                            <Form.Item<mdlEntryListFilterRequest>
                                                                                hidden={true}
                                                                                label={t("entryList.pageSize")}
                                                                                name="pageSize"
                                                                            >
                                                                                <Input />
                                                                            </Form.Item>
                                                                            <Form.Item<mdlEntryListFilterRequest>
                                                                                hidden={true}
                                                                                label={t("entryList.pageNumber")}
                                                                                name="pageNumber"
                                                                            >
                                                                                <Input />
                                                                            </Form.Item>
                                                                            <Col span={6}>
                                                                                <Form.Item< mdlEntryListFilterRequest>
                                                                                    label={t("entryList.confirmStatus")}
                                                                                    name="confirmStatuses"
                                                                                    rules={[{ required: true, message: t("common.required") }]}
                                                                                >
                                                                                    <ConfirmStatusSelect mode="multiple" specificTypes={[enmConfirmationStatus.No, enmConfirmationStatus.Yes, enmConfirmationStatus.Wait]} />
                                                                                </Form.Item>
                                                                            </Col>

                                                                            <Col span={6}>
                                                                                <Form.Item<mdlEntryListFilterRequest>
                                                                                    label={t("entryList.agencies")}
                                                                                    name="agencies"
                                                                                    rules={[{ required: true, message: t("common.required") }]}
                                                                                >
                                                                                    <AgencySelectByHotelPaxId dataSource={agenciesData?.body?.data} crossCheck={true} mode="multiple" hotelIds={[selectedHotel]} maxTagCount={3} />
                                                                                </Form.Item>
                                                                            </Col>
                                                                            <Col span={3}>
                                                                                <Form.Item<mdlEntryListFilterRequest>
                                                                                    label={t("entryList.dateFilterType")}
                                                                                    name="dateFilterType"
                                                                                    rules={[{ required: true, message: t("common.required") }]}
                                                                                >
                                                                                    <Select
                                                                                        size="middle"
                                                                                        onFocus={onFocus}
                                                                                        onChange={(value: string) => handleDateFilterTypeChange(value)}
                                                                                        showSearch
                                                                                        placeholder={t("entryList.dateFilterType")}
                                                                                        filterOption={(input, option) =>
                                                                                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                                                                        }
                                                                                        options={[{ "id": 1, "name": t("entryList.checkIn") }, { "id": 2, "name": t("entryList.checkOut") }]?.map(h => Object.assign(h, {
                                                                                            label: h.name,
                                                                                            value: h.id
                                                                                        }))}
                                                                                    />
                                                                                </Form.Item>
                                                                            </Col>
                                                                            <Col span={4}>
                                                                                <Form.Item label={t("entryList.dateRange")}>
                                                                                    <Space direction="horizontal" size="middle">
                                                                                        <Form.Item<mdlEntryListFilterRequest>
                                                                                            label={t("entryList.from")}
                                                                                            name={dateFilterType == "1" ? "checkInFrom" : "checkOutFrom"}
                                                                                            rules={[{ required: true, message: t("common.required") }]}
                                                                                            style={{ marginBottom: 0 }}
                                                                                            noStyle
                                                                                        >
                                                                                            <SFDatePicker
                                                                                                onChange={(date: Dayjs) => {
                                                                                                    if (date) {
                                                                                                        form.setFieldValue(dateFilterType == "1" ? "checkInFrom" : "checkOutFrom", new Date(
                                                                                                            Date.UTC(
                                                                                                                date.year(),
                                                                                                                date.month(),
                                                                                                                date.date(),
                                                                                                                date.hour(),
                                                                                                                date.minute(),
                                                                                                                date.second()
                                                                                                            )
                                                                                                        ));
                                                                                                    } else {
                                                                                                        form.setFieldValue(dateFilterType == "1" ? "checkInFrom" : "checkOutFrom", null);
                                                                                                    }
                                                                                                }}
                                                                                            />
                                                                                        </Form.Item>

                                                                                        <Form.Item<mdlEntryListFilterRequest>
                                                                                            label={t("entryList.to")}
                                                                                            name={dateFilterType == "1" ? "checkInTo" : "checkOutTo"}
                                                                                            rules={[{ required: true, message: t("common.required") }]}
                                                                                            style={{ marginBottom: 0 }}
                                                                                            noStyle
                                                                                        >
                                                                                            <SFDatePicker
                                                                                                onChange={(date: Dayjs) => {
                                                                                                    if (date) {
                                                                                                        form.setFieldValue(dateFilterType == "1" ? "checkInTo" : "checkOutTo", new Date(
                                                                                                            Date.UTC(
                                                                                                                date.year(),
                                                                                                                date.month(),
                                                                                                                date.date(),
                                                                                                                date.hour(),
                                                                                                                date.minute(),
                                                                                                                date.second()
                                                                                                            )
                                                                                                        ));
                                                                                                    } else {
                                                                                                        form.setFieldValue(dateFilterType == "1" ? "checkInTo" : "checkOutTo", null);
                                                                                                    }
                                                                                                }}
                                                                                            />
                                                                                        </Form.Item>
                                                                                    </Space>
                                                                                </Form.Item>
                                                                            </Col>
                                                                        </>
                                                                    }
                                                                </Row>
                                                                <Row >
                                                                    <Form.Item style={{ width: "100%" }}>
                                                                        <Flex align={"center"} justify="flex-end">
                                                                            <Space>
                                                                                <Button type="primary" htmlType="submit" icon={<SyncOutlined />} size="middle">
                                                                                    {t("common.search")}
                                                                                </Button>
                                                                                {
                                                                                    data && data.body && data.body.data
                                                                                    &&
                                                                                    <>
                                                                                        <Button type="primary" onClick={() => exportToPDF(enmPdfExportType.PDF, data.body!.data!)} icon={<PdfIcon />} size="middle">
                                                                                            {t("resTable.exportToPDF")}
                                                                                        </Button>
                                                                                        <Button type="primary" onClick={() => setOpenExcelExportModal(true)} icon={<WindowsOutlined />}>{t("common.exportToExcel")}</Button>
                                                                                    </>
                                                                                }
                                                                            </Space>
                                                                        </Flex>
                                                                    </Form.Item>
                                                                </Row>
                                                            </Form>
                                                        </Flex>
                                                    }
                                                </div>
                                            </div>
                                            {
                                                isLoading
                                                &&
                                                <div className="row g-gs">
                                                    <div className="col-12">
                                                        <div className="card card-bordered">
                                                            <div className="card-inner">
                                                                <>
                                                                    <Skeleton active />
                                                                    <Skeleton active />
                                                                    <Skeleton active />
                                                                </>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            }

                                            {
                                                data && data.body && data.body.data
                                                &&
                                                <>
                                                    <div className="row g-gs">
                                                        <div className="col-12">
                                                            <div className="card card-bordered">
                                                                <div className="card-inner">
                                                                    <EntryListTable
                                                                        entries={reservationViews} />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </>
                                            }
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Spin>
                </div>
            </div>
            <SFModal
                width={window.innerWidth * 60 / 100}
                open={openExcelExportModal}
                toggler={() => { setOpenExcelExportModal(!openExcelExportModal) }}
                body={<ExportModal excelExportType={enmExcelExportType.EntryList} name="entrylist" data={reservationViews} fnModalClose={() => setOpenExcelExportModal(!openExcelExportModal)} />}
                title={t("common.exportToExcel")}
            />
        </>

    );
});
